import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../common/NotificationBell';

// Try to import logo, fallback to null if not found
let logo;
try {
  logo = require('../../assets/logo.png');
} catch (e) {
  logo = null;
}

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Activities', path: '/activities' },
    { label: 'Events', path: '/events' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Donate', path: '/donate' },
    { label: 'Members', path: '/members' },
    { label: 'Downloads', path: '/downloads' },
    { label: 'Legal & Bank', path: '/legal' },
    { label: 'Contact', path: '/contact' },
  ];

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        pt: 2,
        pb: 3,
        textAlign: 'left',
      }}
    >
      {logo ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: 2,
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="GBCN Logo"
            sx={{
              height: 28,
              width: 'auto',
              objectFit: 'contain',
              mr: 1,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
            }}
          >
            GBCN Foundation
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #ff6600 0%, #ff8533 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            px: 2,
          }}
        >
          GBCN
        </Typography>
      )}
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            sx={{
              py: 0.75,
              px: 2.25,
              '&:hover': {
                backgroundColor: 'rgba(249,115,22,0.06)',
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: location.pathname === item.path ? 600 : 500,
                color: location.pathname === item.path ? '#f97316' : '#111827',
              }}
            />
          </ListItem>
        ))}
        {isAuthenticated ? (
          <>
            <ListItem
              component={Link}
              to="/dashboard"
              sx={{ py: 1, px: 2.5 }}
            >
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem
              component={Link}
              to="/transparency"
              sx={{ py: 1, px: 2.5 }}
            >
              <ListItemText primary="Transparency" />
            </ListItem>
            <ListItem
              button
              onClick={handleLogout}
              sx={{ py: 1, px: 2.5 }}
            >
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              component={Link}
              to="/login"
              sx={{ py: 1, px: 2.5 }}
            >
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              component={Link}
              to="/register"
              sx={{ py: 1, px: 2.5 }}
            >
              <ListItemText
                primary="Join as Member"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: { xs: 'none', md: 'blur(10px)' },
          boxShadow: '0 1px 6px rgba(15, 23, 42, 0.04)',
          borderBottom: '1px solid #e5e7eb',
          color: '#111827',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 48, md: 64 },
            px: { xs: 1.25, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {logo ? (
            <Box
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                justifyContent: isMobile ? 'center' : 'flex-start',
                mr: isMobile ? 0 : 0,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="GBCN Foundation Logo"
                sx={{
                  height: { xs: 26, md: 40 },
                  width: 'auto',
                  objectFit: 'contain',
                  mr: 1,
                }}
              />
              {!isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                    }}
                  >
                    GBCN Foundation
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#6b7280', fontSize: '0.68rem' }}
                  >
                    Global Community Network
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography
              variant="h5"
              component={Link}
              to="/"
              sx={{
                flexGrow: isMobile ? 0 : 1,
                fontWeight: 700,
                textDecoration: 'none',
                color: '#f97316',
                mr: isMobile ? 2 : 0,
              }}
            >
              GBCN Foundation
            </Typography>
          )}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1.25, flexGrow: 1, ml: 3 }}>
              {navItems.map((item) => (
               
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    px: 1,
                    position: 'relative',
                    opacity: location.pathname === item.path ? 1 : 0.8,
                    '&:hover': {
                      color: '#f97316',
                      backgroundColor: 'transparent',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 4,
                      height: 2,
                      borderRadius: 999,
                      backgroundColor: '#f97316',
                      transform:
                        location.pathname === item.path ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform 0.2s ease-out',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {isAuthenticated && <NotificationBell />}
              {isAuthenticated ? (
                <>
                  <IconButton
                    size="large"
                    onClick={handleMenu}
                    color="inherit"
                  >
                    <AccountCircle />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    <MenuItem onClick={() => { navigate('/dashboard'); handleClose(); }}>
                      Dashboard
                    </MenuItem>
                    <MenuItem onClick={() => { navigate('/transparency'); handleClose(); }}>
                      Transparency
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    component={Link}
                    to="/donate"
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#f97316',
                      boxShadow: '0 6px 18px rgba(249, 115, 22, 0.35)',
                      mr: 0.5,
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#ea580c',
                        boxShadow: '0 8px 22px rgba(234, 88, 12, 0.45)',
                      },
                    }}
                  >
                    Donate
                  </Button>
                  <Button
                    component={Link}
                    to="/register"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#111827',
                      color: '#111827',
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#111827',
                        backgroundColor: 'rgba(15,23,42,0.05)',
                      },
                    }}
                  >
                    Join
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 360,
            backgroundColor: '#ffffff',
            color: '#111827',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;

